import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import { DbMetadata } from '../../models/DbMetadata';
import { DbSearchParameters } from '../../models/DbSearchParameters';
import DbSearchFormComponent from './DbSearchFormComponent';
import './database.css';

interface DbIndexProperties {
   num_entries: number;
   db_metadata: DbMetadata;
}

export type { DbIndexProperties };

const AhojDb = (): React.JSX.Element => {
   const defaultProps: DbIndexProperties = {
      num_entries: 0,
      db_metadata: { version: '', release_date: '', description: '' },
   };
   const [properties, setProperties] = useState<DbIndexProperties>(defaultProps);
   const [urlParams] = useSearchParams();
   const presetUniprot = urlParams.get('uniprot_ids') ?? '';
   const initialSearch = useMemo(() => {
      const params = getDefaultSearchParams();
      if (presetUniprot) params.query.uniprot_ids = presetUniprot;
      return params;
   }, [presetUniprot]);
   const [searchParams, setSearchParams] = useState<DbSearchParameters>(initialSearch);

   function normalize(value?: Partial<DbIndexProperties>): DbIndexProperties {
      return {
         ...defaultProps,
         ...value,
         db_metadata: { ...defaultProps.db_metadata, ...value?.db_metadata },
      };
   }

   function getDefaultSearchParams(): DbSearchParameters {
      return {
         query: {
            pdb_ids: '',
            uniprot_ids: '',
            ligands: '',
            xray_only: false,
            exclude_nmr: false,
            res_threshold: '',
            params_url: '',
         },
         empty: true,
         entries: [],
         executed: false,
         showing_limited: false,
         summary: {
            num_entries: 0,
            num_unique_pdb_ids: 0,
            num_unique_uniprot_ids: 0,
            num_unique_target_ligands: 0,
         },
      };
   }

   useEffect(() => {
      apiFetch<DbIndexProperties>('/api/db')
         .then((data: DbIndexProperties) => {
            if (!data) {
               setProperties(defaultProps);
               return;
            }
            setProperties(normalize(data));
         })
         .catch(() => setProperties(defaultProps));
   }, []);

   return (
      <article className="md-content__inner md-typeset">
         <div id="db-header-wrapper">
            <div id="db-hearer-box" style={{ textAlign: 'center' }}>
               <div style={{ display: 'block' }}>
                  <h1 style={{ fontSize: '3em', marginBottom: '0.1em', display: 'inline' }}>AHoJ-DB</h1>
               </div>
               <p style={{ marginTop: '0.1em', fontSize: '0.7rem' }}>
                  <b>AHoJ-DB</b> is a database of precomputed <b>Apo-Holo search results</b> for biologically relevant
                  ligands from <a href="https://zhanggroup.org/BioLiP/index.cgi">BioLiP2</a> database. Each entry
                  represents a target PDB chain and a bound ligand and contains <b>a list of matching structures</b>{' '}
                  that are labeled as HOLO (bound) or APO (unbound)
                  <b>with respect to the binding site</b> defined by the ligand. To search by sequence similarity
                  instead, use <Link to="/db/seqsim">sequence search</Link>.
               </p>
            </div>

            <div id="db-info-box">
               <h2>DB Info</h2>
               <div className="examples">
                  <div>Version:&nbsp;&nbsp;{properties.db_metadata?.version}</div>
                  <div>Released:&nbsp;&nbsp;{properties.db_metadata?.release_date}</div>
                  <div>Entries:&nbsp;&nbsp;{properties.num_entries}</div>
               </div>
               <div id="archive-link-box">
                  <a href="/db/archive">download archive</a>
               </div>
            </div>
         </div>
         <div style={{ float: 'revert' }}></div>
         <DbSearchFormComponent
            key={presetUniprot || 'default'}
            value={presetUniprot ? initialSearch : searchParams}
            autoSearch={Boolean(presetUniprot)}
            onChange={setSearchParams}
         ></DbSearchFormComponent>
         <br />
         <br />
         <br />
      </article>
   );
};

export default AhojDb;
